import { useMemo, useRef, useState } from 'react';
import type { BuildState } from '@/types';
import type { UseBuildState } from '@/hooks/useBuildState';
import { deriveAllWithStability } from '@/lib/calculations';
import { Card } from '@/components/ui/Card';
import { SVGDiagram } from './SVGDiagram';
import { RealisticArrow, DEFAULT_APPEARANCE, type Appearance } from './RealisticArrow';
import { AppearancePicker } from './AppearancePicker';
import { KillVerdict } from './KillVerdict';
import { RangingForgiveness } from './RangingForgiveness';
import { ShareCard } from './ShareCard';
import { EmailCaptureModal, getCachedLead } from './EmailCaptureModal';
import { exportSvgElementToPng, slugify } from '@/lib/exportImage';
import { MetricsCards } from './MetricsCards';
import { FOCBar } from './FOCBar';
import { WeightBreakdown } from './WeightBreakdown';
import { SpineCheck } from './SpineCheck';
import { TrajectoryTable } from './TrajectoryTable';
import { StabilityCard } from './StabilityCard';
import { WarningsPanel } from './WarningsPanel';
import { DiagramBanner } from './ui/DiagramBanner';

interface LivePreviewProps {
  state: BuildState;
  build: UseBuildState;
}

/**
 * Right-hand preview area. Owns the single derive pass and propagates `derived`
 * to every dependent panel.
 */
export function LivePreview({ state, build }: LivePreviewProps) {
  const derived = useMemo(() => deriveAllWithStability(state), [state]);
  const offset = derived.balancePoint - state.length / 2;
  const [view, setView] = useState<'blueprint' | 'render'>('blueprint');
  const [appearance, setAppearance] = useState<Appearance>(DEFAULT_APPEARANCE);
  const shareRef = useRef<SVGSVGElement>(null);
  const [emailGate, setEmailGate] = useState(false);

  const downloadShareCard = () => {
    if (shareRef.current) {
      void exportSvgElementToPng(shareRef.current, `${slugify(state.name || 'arrow')}-sparrow-card.png`, 2);
    }
  };
  // Export gate: free to build, email required to download the shareable card.
  const handleShareClick = () => {
    if (getCachedLead()) downloadShareCard();
    else setEmailGate(true);
  };

  return (
    <section className="space-y-4">
      {/* Top: Build Review (the aggregated warnings panel) */}
      <WarningsPanel state={state} derived={derived} />

      {/* Kill-confidence verdict — the "will it kill, and how far" centerpiece */}
      <KillVerdict state={state} derived={derived} />

      {/* Ranging forgiveness — how far you can mis-range and still hit vitals */}
      <RangingForgiveness state={state} derived={derived} />

      {/* Arrow diagram + quick stats */}
      <Card padding="lg" accent>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">Live Arrow Diagram</h2>
          <div className="flex items-center gap-3">
            {view === 'blueprint' && (
              <div className="hidden sm:block text-xs font-mono text-zinc-500">
                <span className="text-ember-500">●</span> CG &nbsp;
                <span className="text-violet-400">●</span> CP &nbsp;
                <span className="text-zinc-500">┃</span> Physical Center
              </div>
            )}
            <div
              className="inline-flex font-mono"
              style={{ border: '1px solid #3f3f46', borderRadius: 6, overflow: 'hidden' }}
            >
              {(['blueprint', 'render'] as const).map((m) => {
                const on = view === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setView(m)}
                    aria-pressed={on}
                    style={{
                      padding: '4px 11px',
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      background: on ? '#f59e0b' : 'transparent',
                      color: on ? '#18181b' : '#a1a1aa',
                      fontWeight: on ? 600 : 400,
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-zinc-950 rounded-lg overflow-hidden">
          <DiagramBanner
            id={view === 'render' ? 'FLT-LIVE · RENDER VIEW' : 'FLT-LIVE · ARROW ASSEMBLY'}
            doc="SE-FLT"
            status="compute"
            statusLabel={view === 'render' ? 'RENDER' : 'LIVE SOLVE'}
          />
          <div className="p-4 overflow-x-auto scrollbar-thin">
            {view === 'blueprint' ? (
              <SVGDiagram state={state} derived={derived} />
            ) : (
              <RealisticArrow state={state} finish={appearance} />
            )}
          </div>
          {view === 'render' && (
            <div className="px-4 pb-4 space-y-4">
              <AppearancePicker value={appearance} onChange={setAppearance} />
              <div className="border-t border-zinc-800 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold">Share card</h3>
                    <p className="text-[11px] text-zinc-500">A clean image of your build for socials.</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleShareClick}
                    style={{ background: '#f59e0b', color: '#18181b', fontWeight: 600, borderRadius: 6, padding: '7px 16px', fontSize: 13, border: 'none', cursor: 'pointer' }}
                  >
                    Download PNG
                  </button>
                </div>
                <div className="max-w-sm mx-auto">
                  <ShareCard ref={shareRef} state={state} derived={derived} finish={appearance} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
          <QuickStat label="Length" value={`${state.length.toFixed(1)}" overall`} />
          <QuickStat label="Balance from Nock" value={`${derived.balancePoint.toFixed(2)}"`} />
          <QuickStat label="Phys. Center" value={`${(state.length / 2).toFixed(2)}"`} />
          <QuickStat
            label="FOC Offset"
            value={`${offset >= 0 ? '+' : ''}${offset.toFixed(2)}"`}
          />
        </div>
      </Card>

      <MetricsCards derived={derived} />
      <FOCBar derived={derived} />
      <SpineCheck state={state} setField={build.setField} />
      <StabilityCard state={state} derived={derived} />
      <TrajectoryTable state={state} derived={derived} />
      <WeightBreakdown state={state} totalWeight={derived.weight} />

      <EmailCaptureModal
        isOpen={emailGate}
        onClose={() => setEmailGate(false)}
        onSubmit={() => {
          setEmailGate(false);
          downloadShareCard();
        }}
        softGate
        context="pro-gate"
      />
    </section>
  );
}

function QuickStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-mono text-sm tabular-nums">{value}</div>
    </div>
  );
}
